import { useEffect, useMemo, useState } from 'react';
import { useApp } from '@/hooks/use-app-context';
import { useToast } from '@/hooks/use-toast';
import { useThemeQuestionCuration } from './use-theme-question-curation';
import { EMPTY_THEME_QUESTION, type ThemeQuestionFormValue } from './theme-question-editor.types';
import type { ThemeImportStatusFilter } from '@/api/theme-import-history.api';

const MAX_SELECTION = 200;

export function useThemeQuestionEditor(themeId: string, open: boolean) {
  const { t } = useApp();
  const { toast } = useToast();
  const copy = t.admin.excelFiles.themes.questions;
  const [status, setStatus] = useState<ThemeImportStatusFilter>('active');
  const { query, addMutation, deactivateMutation, reactivateMutation, deleteMutation, importMutation } = useThemeQuestionCuration(themeId, open, status);
  const [form, setForm] = useState<ThemeQuestionFormValue>(EMPTY_THEME_QUESTION);
  const [search, setSearch] = useState('');
  const [domain, setDomain] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isConfirming, setIsConfirming] = useState(false);
  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_THEME_QUESTION);
    setSearch('');
    setDomain('');
    setSelectedIds(new Set());
    setIsConfirming(false);
    setStatus('active');
  }, [open, themeId]);

  const changeStatus = (next: ThemeImportStatusFilter) => {
    setStatus(next);
    setSelectedIds(new Set());
    setIsConfirming(false);
    setSearch('');
    setDomain('');
  };

  useEffect(() => {
    const language = rows.find((row) => row.language)?.language;
    if (open && language) setForm((current) => current.language ? current : { ...current, language });
  }, [open, rows]);

  const domains = useMemo(
    () => [...new Set(rows.map((row) => row.domain).filter((value): value is string => Boolean(value)))].sort(),
    [rows],
  );
  const visible = useMemo(() => {
    const normalized = search.trim().toLocaleLowerCase();
    return rows.filter((row) =>
      (!domain || row.domain === domain) &&
      (!normalized || row.questionText.toLocaleLowerCase().includes(normalized)),
    );
  }, [rows, search, domain]);

  const toggle = (id: string) => setSelectedIds((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else if (next.size < MAX_SELECTION) next.add(id);
    else toast({ title: t.error, description: copy.selectionLimit, variant: 'destructive' });
    return next;
  });

  const toggleVisible = () => setSelectedIds((current) => {
    const next = new Set(current);
    const shouldClear = visible.every((row) => next.has(row.id));
    for (const row of visible) {
      if (shouldClear) next.delete(row.id);
      else if (next.size < MAX_SELECTION) next.add(row.id);
    }
    if (!shouldClear && visible.some((row) => !next.has(row.id))) {
      toast({ title: t.error, description: copy.selectionLimit, variant: 'destructive' });
    }
    return next;
  });

  const add = async () => {
    const result = await addMutation.mutateAsync({
      questionText: form.questionText.trim(),
      language: form.language.trim(),
      ...(form.domain.trim() ? { domain: form.domain.trim() } : {}),
      ...(form.source.trim() ? { source: form.source.trim() } : {}),
    }).catch(() => null);
    if (result) setForm((current) => ({ ...EMPTY_THEME_QUESTION, language: current.language }));
  };

  const deactivateSelected = async () => {
    const result = await deactivateMutation.mutateAsync([...selectedIds]).catch(() => null);
    if (result) {
      setSelectedIds(new Set());
      setIsConfirming(false);
    }
  };

  const reactivateSelected = async () => {
    const result = await reactivateMutation.mutateAsync([...selectedIds]).catch(() => null);
    if (result) {
      setSelectedIds(new Set());
      setIsConfirming(false);
    }
  };

  const deleteSelected = async (password: string): Promise<boolean> => {
    const result = await deleteMutation.mutateAsync({ ids: [...selectedIds], password }).catch(() => null);
    if (!result) return false;
    setSelectedIds(new Set());
    return true;
  };

  const importFile = async (file: File) => {
    if (!/\.(xlsx|xls)$/i.test(file.name) || file.size < 1 || file.size > 5 * 1024 * 1024) {
      toast({ title: t.error, description: copy.invalidFile, variant: 'destructive' });
      return;
    }
    if (!/^[\p{L}\p{M}0-9_-]{1,40}$/u.test(form.language)) {
      toast({ title: t.error, description: copy.invalidLanguage, variant: 'destructive' });
      return;
    }
    await importMutation.mutateAsync({ file, language: form.language }).catch(() => null);
  };

  return {
    query, rows, visible, domains, form, setForm, search, setSearch, domain, setDomain,
    status, changeStatus,
    selectedIds, isConfirming, setIsConfirming, toggle, toggleVisible,
    add, deactivateSelected, reactivateSelected, deleteSelected, importFile, isAdding: addMutation.isPending,
    isDeactivating: deactivateMutation.isPending,
    isReactivating: reactivateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isImporting: importMutation.isPending,
  };
}
